/**
 * The admin console — a desktop-shaped screen at its own address,
 * admin.schnauzelist.com.
 *
 * Doug's call, 21 September, after the first pass landed inside the phone
 * app's account sheet: a screen for scanning 136 venues by group and picking
 * one wants a sidebar tree and a wide panel, not a stack of sheets on a
 * 390px canvas. The phone version stays — `app/src/components/VenueAdmin.tsx`
 * — because Cmd-V and the file-input fallback are exactly right for the
 * phone, and a screen he reaches for on the move should not disappear
 * because a desktop console now exists.
 *
 * A second section, Leads, joined it on 22 September: Doug's ask, once the
 * newsletter catalogue-building pass made him wary of a lead's `place_guess`
 * growing the venue list on its own say — "this should be where management
 * happens." A tab rather than a second address, so signing in once covers
 * both and the venue list a lead's place gets checked against is the exact
 * one this screen already shows.
 */
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../app/src/lib/supabase'
import { fetchUpcomingEventCounts } from '../../app/src/lib/venueRecord'
import { useSession } from './useSession'
import { SignIn } from './SignIn'
import { VenuePanel } from './VenuePanel'
import { LeadsPanel } from './LeadsPanel'
import { EventsPanel } from './EventsPanel'
import { GROUPS, groupOf } from './venueGroups'

export interface Venue {
  id: string
  name: string
  kind: string | null
  city: string | null
  commercial: boolean | null
  hero_key: string | null
  hero_alt: string | null
  hero_credit: string | null
  hero_license: string | null
  // Everything below is read-only in this screen — display only, no field
  // for it in the editor. Doug's ask: "I want to see any data we have in
  // the database per venue", not to make every column editable.
  street: string | null
  postcode: string | null
  district: string | null
  country: string | null
  lat: number | null
  lng: number | null
  site_url: string | null
  specialism: string | null
  publishes: string | null
  source_notes: string | null
  organisation_id: string | null
  created_at: string
}

// One literal, not built with `+`: postgrest-js infers the returned row
// shape from this string at the type level, and concatenation widens it to
// plain `string`, which is exactly the type error that sent me here.
const VENUE_FIELDS =
  'id,name,kind,city,commercial,hero_key,hero_alt,hero_credit,hero_license,street,postcode,district,country,lat,lng,site_url,specialism,publishes,source_notes,organisation_id,created_at'

function useVenues() {
  const [venues, setVenues] = useState<Venue[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function refetch() {
    const r = await supabase
      .from('venues')
      .select(VENUE_FIELDS)
      .is('parent_id', null)
      .order('name')
    if (r.error) setError(r.error.message)
    else { setVenues((r.data ?? []) as Venue[]); setError(null) }
  }

  useEffect(() => { void refetch() }, [])

  return { venues, error, refetch }
}

/** How many upcoming events every venue has, for the whole list at once —
 *  fetched separately from `venues` itself because it is a fundamentally
 *  different query shape (a scan of `feed` and `showing_list`, not a row on
 *  `venues`), and null while in flight is the honest state: not yet known,
 *  not "none". */
function useVenueEvents() {
  const [counts, setCounts] = useState<Map<string, number> | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    fetchUpcomingEventCounts()
      .then(r => { if (live) setCounts(r) })
      .catch(e => { if (live) setError(e instanceof Error ? e.message : String(e)) })
    return () => { live = false }
  }, [])

  return { counts, error }
}

type Tri = 'all' | 'has' | 'missing'
type Sort = 'name' | 'name-desc' | 'added' | 'events-desc' | 'events-asc'

const pct = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0)

export default function App() {
  const { session, loading, adminState, signIn, signOut } = useSession(supabase)

  if (loading) return <div className="gate"><p className="dim">Loading…</p></div>
  if (!session) return <SignIn onSignIn={signIn} />
  // U0.3: a signed-in user who is not an admin gets a refusal and a way out,
  // not a console whose every save the database would refuse (U0.2).
  if (adminState === 'checking') return <div className="gate"><p className="dim">Loading…</p></div>
  if (adminState !== 'admin') {
    return (
      <div className="gate">
        <div className="gate-box">
          <h1>Admin</h1>
          <p>{adminState === 'error'
            ? 'Could not check whether this account is an admin. Try again in a moment.'
            : "This console is for the app's admins."}</p>
          <button type="button" className="btn btn-primary" onClick={() => void signOut()}>Sign out</button>
        </div>
      </div>
    )
  }

  return <Shell email={session.user?.email ?? null} onSignOut={() => void signOut()} />
}

type SectionId = 'venues' | 'events' | 'leads'

const SECTIONS: { id: SectionId; label: string }[] = [
  { id: 'venues', label: 'Venues' },
  { id: 'events', label: 'Events' },
  { id: 'leads', label: 'Leads' },
]

/** The topbar and the tab switch, shared by both sections so signing in and
 *  signing out happens once rather than per-section. */
function Shell({ email, onSignOut }: { email: string | null; onSignOut: () => void }) {
  const [section, setSection] = useState<SectionId>('venues')

  return (
    <div className="shell">
      <header className="topbar">
        <span className="wordmark">schnauzelist / admin</span>
        <nav className="tabs">
          {SECTIONS.map(s => (
            <button key={s.id} className={`tab${section === s.id ? ' is-active' : ''}`}
                    onClick={() => setSection(s.id)}>
              {s.label}
            </button>
          ))}
        </nav>
        <span className="topbar-right">
          {email && <span className="dim">{email}</span>}
          <button className="btn btn-quiet" onClick={onSignOut}>Sign out</button>
        </span>
      </header>

      {section === 'venues' && <VenueConsole />}
      {section === 'events' && <EventsPanel supabase={supabase} />}
      {section === 'leads' && <LeadsPanel />}
    </div>
  )
}

function VenueConsole() {
  const { venues, error, refetch } = useVenues()
  const { counts: eventCounts, error: eventsError } = useVenueEvents()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [photoFilter, setPhotoFilter] = useState<Tri>('all')
  const [eventFilter, setEventFilter] = useState<Tri>('all')
  const [sort, setSort] = useState<Sort>('name')

  const needle = q.trim().toLowerCase()
  const eventCount = (v: Venue) => eventCounts?.get(v.id) ?? 0
  const hasEvents = (v: Venue) => eventCount(v) > 0

  // Search and the two filters narrow the same list the stats bar counts,
  // deliberately: a filter that hid three galleries with no photo and left
  // the stats line still saying "92%" would read as the stats forgetting
  // what was on screen.
  const filtered = useMemo(() => {
    let out = venues ?? []
    if (needle) out = out.filter(v => v.name.toLowerCase().includes(needle))
    if (photoFilter === 'has') out = out.filter(v => !!v.hero_key)
    if (photoFilter === 'missing') out = out.filter(v => !v.hero_key)
    // An event filter is meaningless before eventCounts has loaded — filtering
    // on an empty map would read as "nothing has an event", which is a
    // fact about the fetch still being in flight, not about the venues.
    if (eventFilter !== 'all' && eventCounts) {
      out = out.filter(v => (eventFilter === 'has' ? hasEvents(v) : !hasEvents(v)))
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues, needle, photoFilter, eventFilter, eventCounts])

  const sorted = useMemo(() => {
    const out = [...filtered]
    if (sort === 'name-desc') out.sort((a, b) => b.name.localeCompare(a.name))
    else if (sort === 'added') out.sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    // Ties (most commonly 0-0, most venues have no upcoming event at all)
    // fall back to the name, so "most/fewest events" does not otherwise
    // read as a random shuffle of the majority that tie at zero.
    else if (sort === 'events-desc') out.sort((a, b) => eventCount(b) - eventCount(a) || a.name.localeCompare(b.name))
    else if (sort === 'events-asc') out.sort((a, b) => eventCount(a) - eventCount(b) || a.name.localeCompare(b.name))
    else out.sort((a, b) => a.name.localeCompare(b.name))
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort, eventCounts])

  const byGroup = useMemo(() => {
    const m = new Map<string, Venue[]>()
    for (const g of GROUPS) m.set(g.id, [])
    for (const v of sorted) m.get(groupOf(v.kind))!.push(v)
    return m
  }, [sorted])

  const selected = venues?.find(v => v.id === selectedId) ?? null

  // Unfiltered totals — the stats bar answers "how complete is the
  // catalogue", which the search box and the two filters should not be able
  // to change just by narrowing what is on screen underneath it.
  const all = venues ?? []
  const withPhoto = all.filter(v => !!v.hero_key).length
  const withEvents = eventCounts ? all.filter(hasEvents).length : null

  return (
    <>
      {venues && (
        <div className="statsbar">
          <span><b>{all.length}</b> venues</span>
          <span><b>{pct(withPhoto, all.length)}%</b> have a photo <span className="dim">({all.length - withPhoto} missing)</span></span>
          <span>
            {withEvents == null
              ? <span className="dim">reading upcoming events…</span>
              : <>
                  <b>{pct(withEvents, all.length)}%</b> have an upcoming event{' '}
                  <span className="dim">({all.length - withEvents} with none)</span>
                </>}
          </span>
          {eventsError && <span className="err">{eventsError}</span>}
        </div>
      )}

      <div className="body">
        <nav className="sidebar">
          <input
            className="search"
            placeholder="Search a venue"
            value={q}
            onChange={e => setQ(e.currentTarget.value)}
          />

          <div className="filters">
            <select value={photoFilter} onChange={e => setPhotoFilter(e.target.value as Tri)}>
              <option value="all">Any photo</option>
              <option value="has">Has photo</option>
              <option value="missing">Missing photo</option>
            </select>
            <select value={eventFilter} onChange={e => setEventFilter(e.target.value as Tri)}>
              <option value="all">Any events</option>
              <option value="has">Has upcoming</option>
              <option value="missing">None upcoming</option>
            </select>
            <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
              <option value="name">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
              <option value="added">Recently added</option>
              <option value="events-desc">Most events first</option>
              <option value="events-asc">Fewest events first</option>
            </select>
          </div>

          {/* What the dot next to each row means — added once Doug asked
              what it was, which is the tell that a dot with no key needs
              one. */}
          <p className="legend"><span className="dot has-hero" aria-hidden="true" /> has a photo</p>

          {error && <p className="err">{error}</p>}
          {!venues && !error && <p className="dim">Loading venues…</p>}
          {venues && sorted.length === 0 && <p className="dim">Nothing matches.</p>}

          {GROUPS.map(g => {
            const mine = byGroup.get(g.id) ?? []
            if (!mine.length) return null
            return (
              <details key={g.id} open>
                <summary>
                  {g.label} <span className="dim">{mine.length}</span>
                </summary>
                {mine.map(v => (
                  <button
                    key={v.id}
                    className={`venue-row${v.id === selectedId ? ' is-selected' : ''}`}
                    onClick={() => setSelectedId(v.id)}
                  >
                    <span className={`dot${v.hero_key ? ' has-hero' : ''}`} aria-hidden="true" />
                    <span className="venue-row__name">{v.name}</span>
                    {eventCounts && <span className="venue-row__count">{eventCount(v)}</span>}
                  </button>
                ))}
              </details>
            )
          })}
        </nav>

        <main className="main">
          {selected
            ? (
              <VenuePanel
                key={selected.id}
                supabase={supabase}
                venue={selected}
                onSaved={() => void refetch()}
              />
            )
            : <div className="empty">Pick a venue from the left.</div>}
        </main>
      </div>
    </>
  )
}
